const AdminRoleName = "Admin";

export class AuthorizeService {
  _callbacks: any = [];
  _nextSubscriptionId = 0;
  _user: Record<string, string> | null = null;
  _isAuthenticated = false;

  // By default, pop-ups are disabled because they don't work properly on Edge.
  // If you want to enable pop up authentication simply set this flag to false.

  async isAuthenticated() {
    const user = await this.getUser();
    return !!user;
  }

  async getUser() {
    const userRaw = localStorage.getItem("user");
    this._user = JSON.parse(userRaw as any);
    return this._user;
  }

  async getAccessToken() {
    return this._user && this._user.token;
  }

  getAccessTokenSync() {
    return this._user && this._user.token;
  }

  // We try to authenticate the user in three different ways:
  // 1) We try to see if we can authenticate the user silently. This happens
  //    when the user is already logged in on the IdP and is done using a hidden iframe
  //    on the client.
  // 2) We try to authenticate the user using a PopUp Window. This might fail if there is a
  //    Pop-Up blocker or the user has disabled PopUps.
  // 3) If the two methods above fail, we redirect the browser to the IdP to perform a traditional
  //    redirect flow.
  async signIn(state: any, login: string, password: string) {
    try {

      const loginPayload = {
        login: login,
        password: password,
      };

      const body = JSON.stringify(loginPayload);

      const requestOptions = {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: body
      };

      const response = await fetch('/api/users/authenticate', requestOptions);

      if (!response.ok) {
        if (response.status === 400) {
          const responseBody = await response.json();
          return this.error(responseBody.message);
        }

        return this.error("Произошла ошибка во время авторизации");
      }

      const data = await response.json();

      const role = JSON.parse(window.atob(data.token.split(".")[1])).role;

      const convertStringToArray = (object: any) => {
        if (object === undefined)
          return []

        return (typeof object === 'string')
          ? Array(object)
          : object
      }

      const isAdmin = !!convertStringToArray(role).find((x: string) => x === AdminRoleName)
      const user = {name: data.login, ...data, isAdmin};
      this.updateState(user);
      return this.success(state);
    } catch (silentError) {
      // User might not be authenticated, fallback to popup authentication
      console.log("Silent authentication error: ", silentError);

      return this.error("An error occurred");
      // try {
      //   if (this._popUpDisabled) {
      //     throw new Error('Popup disabled. Change \'AuthorizeService.js:AuthorizeService._popupDisabled\' false to enable it.')
      //   }
      //
      //   const popUpUser = await this.userManager.signingPopup(this.createArguments());
      //   this.updateState(popUpUser);
      //   return this.success(state);
      // } catch (popUpError) {
      //   if (popUpError.message === "Popup window closed") {
      //     // The user explicitly cancelled the login action by closing an opened popup.
      //     return this.error("The user closed the window.");
      //   } else if (!this._popUpDisabled) {
      //     console.log("Popup authentication error: ", popUpError);
      //   }
      //
      //   // PopUps might be blocked by the user, fallback to redirect
      //   try {
      //     await this.userManager.signingRedirect(this.createArguments(state));
      //     return this.redirect();
      //   } catch (redirectError) {
      //     console.log("Redirect authentication error: ", redirectError);
      //     return this.error(redirectError);
      //   }
      // }
    }
  }

  // We try to sign out the user in two different ways:
  // 1) We try to do a sign-out using a PopUp Window. This might fail if there is a
  //    Pop-Up blocker or the user has disabled PopUps.
  // 2) If the method above fails, we redirect the browser to the IdP to perform a traditional
  //    post logout redirect flow.
  async signOut(state: any) {
    try {
      this.updateState(null);
      window.location.href = '/';
      return this.success(state);
    } catch (popupSignOutError) {
      window.location.href = '/';
      return this.error(popupSignOutError as any);
    }
  }

  updateState(user: any) {
    if (!user) {
      localStorage.removeItem("user");
    } else {
      localStorage.setItem("user", JSON.stringify(user));
    }

    this._user = user;
    this._isAuthenticated = !!this._user;
    this.notifySubscribers();
  }

  subscribe(callback: any) {
    this._callbacks.push({callback, subscription: this._nextSubscriptionId++});
    return this._nextSubscriptionId - 1;
  }

  unsubscribe(subscriptionId: string | number) {
    const subscriptionIndex = this._callbacks
      .map((element: Record<string, string>, index: string) => element.subscription === subscriptionId ? {found: true, index} : {found: false})
      .filter((element: any) => element.found === true);
    if (subscriptionIndex.length !== 1) {
      throw new Error(`Found an invalid number of subscriptions ${subscriptionIndex.length}`);
    }

    this._callbacks.splice(subscriptionIndex[0].index, 1);
  }

  notifySubscribers() {
    for (let i = 0; i < this._callbacks.length; i++) {
      const callback = this._callbacks[i].callback;
      callback();
    }
  }

  error(message: string) {
    return {status: AuthenticationResultStatus.Fail, message};
  }

  success(state: any) {
    return {status: AuthenticationResultStatus.Success, state};
  }

  redirect() {
    return {status: AuthenticationResultStatus.Redirect};
  }
}

const authService = new AuthorizeService();

export default authService;

export const AuthenticationResultStatus = {
  Redirect: 'redirect',
  Success: 'success',
  Fail: 'fail'
};
